/**
 * Real-time video (LiveKit) wire shapes for the mobile app — the mobile
 * counterpart of the web venue's video flows
 * (`apps/web/src/app/core/services/video-hub.service.ts`).
 *
 * Two attendee capabilities ride these types:
 *   1. **Networking 1:1 calls** — a private LiveKit room (`private-{a}-{b}`)
 *      negotiated over `EngagementHub` (ring/accept/decline) with the
 *      credentials minted by `VideoHub.JoinPrivateCallRoom`.
 *   2. **Live booth rooms** — a multi-party LiveKit room (`booth-{boothId}`)
 *      whose credentials come from `VideoHub.JoinBoothRoom`. Reps publish;
 *      attendees publish only when the booth enables attendee video,
 *      otherwise they join listen-only.
 *
 * The backend (`vEvents.Engagement.Services.LiveKitTokenService`) returns
 * PascalCase keys; the BFF/Engagement SignalR JSON may surface either
 * casing, so consumers normalise both — never hardcode the WSS URL, always
 * use the `serverUrl` the backend hands back.
 */

/** 'video' (camera + mic) or 'audio' (mic-only) call. */
export type CallMediaType = 'video' | 'audio';

/**
 * LiveKit join credentials returned by `VideoHub.JoinBoothRoom` /
 * `JoinPrivateCallRoom`. `canPublish=false` means the client should join
 * the room in listen-only mode (booth attendee with attendee-video off).
 */
export interface LiveKitCredentials {
  token: string;
  serverUrl: string;
  roomName: string;
  canPublish: boolean;
  /** True when the room treats every participant as an equal publisher. */
  networkingMode: boolean;
}

/** Incoming `IncomingCall` payload pushed by `EngagementHub.InitiateCall`. */
export interface IncomingCallNotice {
  callerId: string;
  callerName: string;
  callerRole: string;
  callType: CallMediaType;
  roomName: string;
}

/** `CallAccepted` payload — the callee picked up; the caller now joins. */
export interface CallAcceptedNotice {
  accepterId: string;
  roomName: string;
}

/** `CallDeclined` / `CallEnded` / `CallCancelled` payload. */
export interface CallRoomNotice {
  roomName: string;
}

/**
 * Deterministic private-call room name shared by both parties so each side
 * derives the *same* LiveKit room without a round-trip. Mirrors the web
 * venue's `generatePrivateRoomName` (`private-{lowerId}-{higherId}`); ids
 * are compared numerically when possible, otherwise lexicographically.
 */
export function privateRoomName(a: string, b: string): string {
  const na = Number(a);
  const nb = Number(b);
  const numeric = Number.isFinite(na) && Number.isFinite(nb);
  const [first, second] = numeric
    ? (na <= nb ? [a, b] : [b, a])
    : ([a, b].sort());
  return `private-${first}-${second}`;
}
